import { useState } from "react";
import { ArrowLeft, MapPin } from "lucide-react";
import { FEATURED_WORK, WORK_CITIES } from "#/lib/constants";
import { img } from "#/lib/images";

type City = (typeof WORK_CITIES)[number];

const ZOOM = 2.4;

/**
 * Delaware outline traced from real boundary points (lon/lat projected onto
 * a 200x400 viewBox): Twelve-Mile-Circle arc up top, the New Castle pinch,
 * then the bay coast widening south to Fenwick. Simplified, not survey data.
 */
const DELAWARE_PATH = `M 3 37
  C 8 20 20 8 34 6
  C 55 3 74 4 86 10
  C 92 13 95 15 95 18
  C 90 26 85 32 79 37
  C 72 43 66 48 61 54
  C 60 62 60 70 60 77
  C 59 83 58 88 58 94
  C 63 113 74 131 87 149
  C 94 157 100 164 105 171
  C 105 186 103 200 103 214
  C 111 233 121 252 132 269
  C 145 283 160 296 174 306
  C 180 302 185 300 187 302
  C 191 320 195 350 197 374
  L 197 400 L 3 400 Z`;

const LABEL_POS = {
  left: "right-full mr-1.5 top-1/2 -translate-y-1/2",
  right: "left-full ml-1.5 top-1/2 -translate-y-1/2",
  top: "bottom-full mb-1 left-1/2 -translate-x-1/2",
  bottom: "top-full mt-1 left-1/2 -translate-x-1/2",
} as const;

/**
 * Interactive work map for /our-work: HTML marker buttons overlaid at
 * viewBox percentages so they zoom with the map (CSS transform, no map
 * library). Click a city and the panel lists its FEATURED_WORK projects.
 */
export function DelawareWorkMap() {
  const [active, setActive] = useState<City | null>(null);

  const projects = active
    ? FEATURED_WORK.filter((w) => w.location.startsWith(active.name))
    : [];

  return (
    <section className="relative overflow-hidden bg-graphite py-14 lg:py-20">
      <div className="blueprint-grid absolute inset-0 opacity-60" />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-16 lg:px-8">
        {/* Map */}
        <div className="relative mx-auto aspect-1/2 w-full max-w-sm overflow-hidden sm:max-w-md">
          <div
            className="absolute inset-0 transition-transform duration-700 ease-out motion-reduce:duration-0"
            style={
              active
                ? {
                    transform: `scale(${ZOOM})`,
                    transformOrigin: `${active.x / 2}% ${active.y / 4}%`,
                  }
                : undefined
            }
          >
            <svg viewBox="0 0 200 400" aria-hidden="true" className="h-full w-full">
              <path
                d={DELAWARE_PATH}
                className="fill-flame/10 stroke-flame/60"
                strokeWidth={1.5}
                strokeLinejoin="round"
              />
            </svg>

            {/* City markers (real buttons: focusable, labelled) */}
            {WORK_CITIES.map((city) => {
              const isActive = active?.slug === city.slug;
              const dim = active !== null && !isActive;
              return (
                <button
                  key={city.slug}
                  type="button"
                  disabled={!city.clickable}
                  aria-label={
                    city.clickable
                      ? `Show our work in ${city.name}, DE`
                      : `${city.name}, DE (projects coming soon)`
                  }
                  onClick={() => setActive(isActive ? null : city)}
                  className={`absolute flex h-3 w-3 -translate-x-1/2 -translate-y-1/2 items-center justify-center transition-opacity duration-500 ${
                    dim ? "opacity-20" : "opacity-100"
                  } ${city.clickable ? "cursor-pointer" : "cursor-default"}`}
                  style={{ left: `${city.x / 2}%`, top: `${city.y / 4}%` }}
                >
                  {city.clickable && !active && (
                    <span className="absolute h-full w-full animate-ping rounded-full bg-flame/50 motion-reduce:hidden" />
                  )}
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      city.clickable ? "bg-flame" : "bg-white/30"
                    } ${isActive ? "ring-2 ring-white" : ""}`}
                  />
                  <span
                    className={`absolute whitespace-nowrap text-[11px] sm:text-[10px] font-bold uppercase tracking-wide ${
                      LABEL_POS[city.labelSide]
                    } ${city.clickable ? "text-white" : "text-white/40"} ${
                      active && !isActive ? "opacity-0" : ""
                    }`}
                  >
                    {city.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Panel */}
        <div className="flex flex-col justify-center">
          {active === null ? (
            <>
              <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
                Pick a city to see the work.
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-concrete">
                Real projects, on real streets, across New Castle County. Tap a
                blue marker on the map, or jump straight to a city:
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {WORK_CITIES.filter((c) => c.clickable).map((city) => (
                  <button
                    key={city.slug}
                    type="button"
                    onClick={() => setActive(city)}
                    className="cursor-pointer rounded-full px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/20 transition-colors hover:bg-flame hover:ring-flame"
                  >
                    {city.name}, DE
                  </button>
                ))}
              </div>
              <p className="mt-6 text-xs text-concrete">
                More areas coming soon: Wilmington, Bear, Glasgow, and Dover.
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="inline-flex cursor-pointer items-center gap-2 self-start text-sm font-semibold text-flame transition-colors hover:text-flame-light"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to full map
              </button>
              <h2 className="mt-4 flex items-center gap-2 font-heading text-2xl font-extrabold text-white sm:text-3xl">
                <MapPin className="h-6 w-6 text-flame" />
                {active.name}, DE
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {projects.map((work) => (
                  <a
                    key={work.title}
                    href={`/services#${work.service}`}
                    className="group overflow-hidden rounded-lg bg-charcoal no-underline ring-1 ring-white/10 transition-all hover:ring-flame/50"
                  >
                    <img
                      src={img(work.img, { w: 640, h: 420 })}
                      alt={`${work.title} in ${work.location}`}
                      width={640}
                      height={420}
                      loading="lazy"
                      decoding="async"
                      className="aspect-3/2 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="p-4">
                      <span className="text-xs font-bold uppercase tracking-wide text-flame">
                        {work.tag}
                      </span>
                      <p className="mt-1 font-heading text-base font-bold text-white">
                        {work.title}
                      </p>
                    </div>
                  </a>
                ))}
                {projects.length === 0 && (
                  <p className="text-sm text-concrete">
                    Photos from {active.name} are on the way.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
